/**
 * BusinessSettings.jsx — Settings page for the business portal.
 *
 * Three tabs:
 *   1. Profile — view/edit business name & description
 *   2. Trust & Safety — moderation state + appeal flow
 *   3. Notifications — per-category/channel preference toggles
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Bell, Save, Loader2, ShieldAlert, Truck, CircleDollarSign, Landmark } from "lucide-react";
import Swal from "../../utils/apparifySwal";
import { PageHeader, BtnPrimary } from "../../components/erp";
import {
  getBusinessProfile,
  updateBusinessProfile,
  getBusinessNotificationSettings,
  updateBusinessNotificationPreferences,
  getBusinessDispatchLocation,
  getDispatchLocationChangeImpact,
  putBusinessDispatchLocation,
  getBusinessCommitmentPolicy,
  updateBusinessCommitmentPolicy,
  getBusinessFeePolicies,
  createBusinessFeePolicyDraft,
  activateBusinessFeePolicy,
} from "../services/businessNotificationService";
import {
  describeOriginChangeImpact,
  describeOriginChangeResult,
  escapeHtml,
} from '../utils/dispatchOriginChange';
import BusinessTrustSafetyTab from "../components/BusinessTrustSafetyTab";
import BusinessGovernmentRegistrations from "../components/BusinessGovernmentRegistrations";
import ConfirmedPinPicker from "../../features/delivery/components/ConfirmedPinPicker";
import DispatchWindowsSummary from "../components/delivery/DispatchWindowsSummary";

const TABS = [
  { key: "profile", label: "Profile", Icon: Building2 },
  { key: "trust", label: "Trust & Safety", Icon: ShieldAlert },
  { key: "government", label: "Government Registrations", Icon: Landmark },
  { key: "delivery", label: "Delivery Setup", Icon: Truck },
  { key: "notifications", label: "Notifications", Icon: Bell },
];

const CATEGORY_LABELS = {
  PROCUREMENT:  "Procurement",
  DELIVERY:     "Delivery & Logistics",
  SUPPLIER_OPS: "Supplier Operations",
};

const CHANNEL_LABELS = { INAPP: "In-App", EMAIL: "Email", SMS: "SMS", PUSH: "Push" };

export default function BusinessSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const normalizedTab = ["dispatch", "delivery-fee"].includes(requestedTab)
    ? "delivery"
    : requestedTab;
  const tab = TABS.some((item) => item.key === normalizedTab) ? normalizedTab : "profile";

  const handleTabChange = (nextTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Manage your business profile, delivery setup, trust status, and notifications" />

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b border-neutral-200 dark:border-neutral-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={[
              "inline-flex shrink-0 items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px",
              tab === t.key
                ? "border-neutral-900 text-neutral-900 dark:border-neutral-100 dark:text-neutral-100"
                : "border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200",
            ].join(" ")}
          >
            <t.Icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab onOpenTrustTab={() => handleTabChange("trust")} />}
      {tab === "trust" && <BusinessTrustSafetyTab />}
      {tab === "government" && <BusinessGovernmentRegistrations />}
      {tab === "delivery" && <DeliverySetupTab />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}

const DAY_LABELS = [
  { value: 1, short: "Mon" }, { value: 2, short: "Tue" }, { value: 3, short: "Wed" },
  { value: 4, short: "Thu" }, { value: 5, short: "Fri" }, { value: 6, short: "Sat" },
  { value: 0, short: "Sun" },
];

function commitmentFormFrom(policy) {
  const window = policy?.settlementWindows?.[0] || {};
  return {
    capacityCeiling: String(policy?.capacityCeiling ?? 12),
    assignmentProblemResponseMinutes: String(policy?.assignmentProblemResponseMinutes ?? 15),
    daysOfWeek: Array.isArray(window.daysOfWeek) ? [...window.daysOfWeek] : [1, 2, 3, 4, 5],
    opensAt: window.opensAt || "08:00",
    closesAt: window.closesAt || "18:00",
    capabilities: Array.isArray(window.capabilities) ? [...window.capabilities] : ["PARCEL", "COD"],
  };
}

/**
 * Mirror of the server's bounds, so an owner gets a field-level message here
 * instead of a failed dispatch hours later. The server stays authoritative.
 */
function validateCommitment(form) {
  const errors = {};
  const capacity = Number(form.capacityCeiling);
  if (!Number.isInteger(capacity) || capacity < 1 || capacity > 12) {
    errors.capacityCeiling = "Choose between 1 and 12 parcels.";
  }
  const minutes = Number(form.assignmentProblemResponseMinutes);
  if (!Number.isInteger(minutes) || minutes < 5 || minutes > 120) {
    errors.assignmentProblemResponseMinutes = "Choose between 5 and 120 minutes.";
  }
  if (!form.daysOfWeek.length) {
    errors.daysOfWeek = "Pick at least one day someone is at the store to receive riders.";
  }
  if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(form.opensAt)) errors.opensAt = "Use a 24-hour time like 08:00.";
  if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(form.closesAt)) errors.closesAt = "Use a 24-hour time like 18:00.";
  if (!errors.opensAt && !errors.closesAt && form.opensAt >= form.closesAt) {
    errors.closesAt = "Closing time must be later the same day than opening time.";
  }
  if (!form.capabilities.length) {
    errors.capabilities = "Choose whether this location receives parcels, cash, or both.";
  }
  return errors;
}

function DeliveryCommitmentPolicyTab({ onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(commitmentFormFrom(null));
  const [errors, setErrors] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getBusinessCommitmentPolicy()
      .then((response) => {
        const payload = response.data || null;
        setData(payload);
        setForm(commitmentFormFrom(payload?.policy));
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (field) => (event) => {
    setForm((previous) => ({ ...previous, [field]: event.target.value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const toggle = (field, value) => () => {
    setForm((previous) => {
      const current = previous[field];
      return {
        ...previous,
        [field]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
      };
    });
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const save = async () => {
    const found = validateCommitment(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await updateBusinessCommitmentPolicy({
        capacityCeiling: Number(form.capacityCeiling),
        assignmentProblemResponseMinutes: Number(form.assignmentProblemResponseMinutes),
        settlementWindow: {
          daysOfWeek: form.daysOfWeek,
          opensAt: form.opensAt,
          closesAt: form.closesAt,
          capabilities: form.capabilities,
        },
      });
      await load();
      onSaved?.();
      Swal.fire({ icon: "success", title: "Delivery rules saved", timer: 1600, showConfirmButton: false });
    } catch (error) {
      const field = error?.response?.data?.details?.field;
      const message = error?.response?.data?.msg || error?.response?.data?.message || "Please try again.";
      if (field) setErrors({ [String(field).split(".").pop()]: message });
      Swal.fire({ icon: "error", title: "Unable to save the delivery rules", text: message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  const policy = data?.policy || null;

  // No policy means no dispatch location yet: the receiving window has to name a
  // real place, so the rules are created with the location in step 1.
  if (!policy) {
    return (
      <div className="max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
        <h3 className="text-base font-semibold">3. Delivery rules</h3>
        <p className="mt-2">
          Save your dispatch location in step 1 first. Your delivery rules are created with it, because they
          need to know where riders return parcels and cash.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
      <div>
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">3. Delivery rules</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          How much work one rider may carry, and when someone is at your store to take parcels and cash back.
          Deliveries are only committed if a rider can get back inside these hours.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Parcels one rider may carry</span>
          <input
            name="capacityCeiling" type="number" min={1} max={12} step={1} inputMode="numeric"
            value={form.capacityCeiling} onChange={set("capacityCeiling")}
            aria-invalid={errors.capacityCeiling ? "true" : undefined}
            className={`${FIELD_CLASS} mt-1.5`}
          />
          {errors.capacityCeiling
            ? <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.capacityCeiling}</p>
            : <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">At most 12. A rider is never given more than this at once.</p>}
        </label>
        <label className="block">
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Time to answer a problem</span>
          <input
            name="assignmentProblemResponseMinutes" type="number" min={5} max={120} step={1} inputMode="numeric"
            value={form.assignmentProblemResponseMinutes} onChange={set("assignmentProblemResponseMinutes")}
            aria-invalid={errors.assignmentProblemResponseMinutes ? "true" : undefined}
            className={`${FIELD_CLASS} mt-1.5`}
          />
          {errors.assignmentProblemResponseMinutes
            ? <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.assignmentProblemResponseMinutes}</p>
            : <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Minutes a rider has to respond before it is escalated.</p>}
        </label>
      </div>

      <fieldset className="space-y-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
        <legend className="px-1 text-sm font-semibold text-neutral-800 dark:text-neutral-100">When riders can return to the store</legend>
        <div>
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Days</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAY_LABELS.map((day) => {
              const active = form.daysOfWeek.includes(day.value);
              return (
                <button
                  key={day.value} type="button" onClick={toggle("daysOfWeek", day.value)}
                  aria-pressed={active}
                  className={`min-h-11 min-w-14 rounded-xl border px-3 text-sm font-semibold transition ${active
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-neutral-300 bg-white text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"}`}
                >
                  {day.short}
                </button>
              );
            })}
          </div>
          {errors.daysOfWeek && <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.daysOfWeek}</p>}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Opens</span>
            <input name="opensAt" type="time" value={form.opensAt} onChange={set("opensAt")}
              aria-invalid={errors.opensAt ? "true" : undefined} className={`${FIELD_CLASS} mt-1.5`} />
            {errors.opensAt && <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.opensAt}</p>}
          </label>
          <label className="block">
            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Closes</span>
            <input name="closesAt" type="time" value={form.closesAt} onChange={set("closesAt")}
              aria-invalid={errors.closesAt ? "true" : undefined} className={`${FIELD_CLASS} mt-1.5`} />
            {errors.closesAt && <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.closesAt}</p>}
          </label>
        </div>
        <div>
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">This location takes back</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {[["PARCEL", "Parcels"], ["COD", "Cash"]].map(([value, label]) => {
              const active = form.capabilities.includes(value);
              return (
                <button
                  key={value} type="button" onClick={toggle("capabilities", value)} aria-pressed={active}
                  className={`min-h-11 rounded-xl border px-4 text-sm font-semibold transition ${active
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-neutral-300 bg-white text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {errors.capabilities
            ? <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors.capabilities}</p>
            : !form.capabilities.includes("COD") && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Cash-on-delivery orders cannot be committed while this location does not take cash back.
              </p>
            )}
        </div>
      </fieldset>

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm dark:border-neutral-700 dark:bg-neutral-800/60">
        <p className="font-semibold text-neutral-800 dark:text-neutral-100">Set by Apparify</p>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          Timing allowances the system uses to forecast whether a delivery can finish in time. Not editable.
        </p>
        <ul className="mt-2 grid gap-1 text-neutral-600 dark:text-neutral-300 sm:grid-cols-2">
          <li>Pickup buffer &mdash; {policy.pickupDeadline?.bufferFloorMinutes} min + {Math.round((policy.pickupDeadline?.bufferPercent || 0) * 100)}% of travel</li>
          <li>Reminder &mdash; {policy.pickupDeadline?.reminderLeadMinutes} min before due</li>
          <li>Handling per parcel &mdash; {Math.round((policy.serviceSecondsPerParcel || 0) / 60)} min</li>
          <li>Settlement allowance &mdash; {Math.round((policy.settlementHandlingSeconds || 0) / 60)} min</li>
        </ul>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Version {policy.versionNo} is in use. Trips already committed keep the rules they started under.
        </p>
        <BtnPrimary onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save rules
        </BtnPrimary>
      </div>
    </div>
  );
}

function DeliverySetupTab() {
  const [rulesVersion, setRulesVersion] = useState(0);
  return (
    <div className="space-y-6">
      <div className="max-w-2xl rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100">
        <h3 className="font-semibold">Complete these steps before shoppers can receive delivery quotes.</h3>
        <p className="mt-1 leading-relaxed text-blue-800 dark:text-blue-200">
          Set the store location where deliveries begin, then save and activate delivery pricing. Your delivery
          rules are created with the location; review them so riders can return parcels and cash when someone is there.
        </p>
      </div>
      <DispatchLocationTab />
      <DeliveryFeePolicyTab />
      <DeliveryCommitmentPolicyTab onSaved={() => setRulesVersion((value) => value + 1)} />
      <DispatchWindowsSummary refreshKey={rulesVersion} />
    </div>
  );
}

// What a new store starts with. 30 km reaches essentially all of Cavite from a
// centrally located store; 12 km covers its own and neighbouring municipalities.
const ZONE_DEFAULTS = {
  nearRadiusKm: "12",
  nearFee: "50.00",
  serviceRadiusKm: "30",
  farFee: "90.00",
  freeDeliveryOver: "",
};

const FIELD_CLASS = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100";

function peso(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `\u20b1${numeric.toFixed(2)}` : "\u2014";
}

/**
 * Mirror of the server's bounds so the owner gets a useful message instead of a
 * rejected request. The server stays authoritative.
 */
function validateZones(form) {
  const errors = {};
  const near = Number(form.nearRadiusKm);
  const nearFee = Number(form.nearFee);
  if (!Number.isFinite(near) || near < 0.1 || near > 500) {
    errors.nearRadiusKm = "Enter a distance between 0.1 and 500 km.";
  }
  if (!Number.isFinite(nearFee) || nearFee < 0 || nearFee > 100000) {
    errors.nearFee = "Enter a fee between \u20b10 and \u20b1100,000.";
  }
  if (form.hasFarZone) {
    const radius = Number(form.serviceRadiusKm);
    const farFee = Number(form.farFee);
    if (!Number.isFinite(radius) || radius > 500) {
      errors.serviceRadiusKm = "Enter a distance up to 500 km.";
    } else if (Number.isFinite(near) && radius <= near) {
      errors.serviceRadiusKm = "The far zone has to reach further than the near zone.";
    }
    if (!Number.isFinite(farFee) || farFee < 0 || farFee > 100000) {
      errors.farFee = "Enter a fee between \u20b10 and \u20b1100,000.";
    }
  }
  if (form.hasFreeDelivery) {
    const threshold = Number(form.freeDeliveryOver);
    if (!Number.isFinite(threshold) || threshold <= 0 || threshold > 10000000) {
      errors.freeDeliveryOver = "Enter an order total above \u20b10.";
    }
  }
  return errors;
}

function toPayload(form) {
  return {
    currency: "PHP",
    nearRadiusKm: String(form.nearRadiusKm),
    nearFee: Number(form.nearFee).toFixed(2),
    serviceRadiusKm: String(form.hasFarZone ? form.serviceRadiusKm : form.nearRadiusKm),
    farFee: form.hasFarZone ? Number(form.farFee).toFixed(2) : null,
    freeDeliveryOver: form.hasFreeDelivery ? Number(form.freeDeliveryOver).toFixed(2) : null,
  };
}

function formFromPolicy(policy) {
  if (!policy) return { ...ZONE_DEFAULTS, hasFarZone: true, hasFreeDelivery: false };
  const hasFar = policy.farFee != null;
  return {
    nearRadiusKm: String(Number(policy.nearRadiusKm)),
    nearFee: Number(policy.nearFee).toFixed(2),
    serviceRadiusKm: String(Number(hasFar ? policy.serviceRadiusKm : ZONE_DEFAULTS.serviceRadiusKm)),
    farFee: hasFar ? Number(policy.farFee).toFixed(2) : ZONE_DEFAULTS.farFee,
    freeDeliveryOver: policy.freeDeliveryOver != null ? Number(policy.freeDeliveryOver).toFixed(2) : "",
    hasFarZone: hasFar,
    hasFreeDelivery: policy.freeDeliveryOver != null,
  };
}

function requestKey(prefix) {
  return window.crypto?.randomUUID?.()
    || `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function DeliveryFeePolicyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...ZONE_DEFAULTS, hasFarZone: true, hasFreeDelivery: false });
  const [errors, setErrors] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getBusinessFeePolicies()
      .then((response) => {
        const payload = response.data || null;
        setData(payload);
        setForm(formFromPolicy(payload?.drafts?.[0] || payload?.active || null));
      })
      .catch(() => Swal.fire({ icon: "error", title: "Unable to load delivery pricing" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const saveDraft = async () => {
    const found = validateZones(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await createBusinessFeePolicyDraft(toPayload(form), requestKey("fee-draft"));
      await load();
      Swal.fire({ icon: "success", title: "Pricing saved as a draft", text: "Activate it to start quoting shoppers at these prices.", timer: 2200, showConfirmButton: false });
    } catch (error) {
      const field = error?.response?.data?.details?.field;
      if (field) setErrors({ [field]: error?.response?.data?.msg || "This value was rejected." });
      Swal.fire({ icon: "error", title: "Unable to save the pricing", text: error?.response?.data?.msg || error?.response?.data?.message || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const activate = async (policyId) => {
    setSaving(true);
    try {
      await activateBusinessFeePolicy(policyId, requestKey("fee-activate"));
      await load();
      Swal.fire({ icon: "success", title: "Delivery pricing is live", text: "Shoppers now see these prices before they check out." });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to activate the pricing", text: error?.response?.data?.msg || error?.response?.data?.message || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  const active = data?.active || null;
  const latestDraft = data?.drafts?.[0] || null;
  const near = Number(form.nearRadiusKm);
  const outerKm = form.hasFarZone ? Number(form.serviceRadiusKm) : near;
  const zonesCoherent = Number.isFinite(near) && near > 0
    && Number.isFinite(outerKm) && outerKm >= near
    && (!form.hasFarZone || outerKm > near);

  const field = (name, label, hint, props = {}) => (
    <label className="block">
      <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">{label}</span>
      <input
        name={name}
        value={form[name]}
        onChange={set(name)}
        inputMode="decimal"
        aria-invalid={errors[name] ? "true" : undefined}
        aria-describedby={errors[name] ? `${name}-error` : `${name}-hint`}
        className={`${FIELD_CLASS} mt-1.5`}
        {...props}
      />
      {errors[name]
        ? <p id={`${name}-error`} className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{errors[name]}</p>
        : <p id={`${name}-hint`} className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    </label>
  );

  return (
    <div className="max-w-2xl space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
      <div>
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">2. Delivery pricing</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Set one price for nearby deliveries and, if you want to reach further, a second price for the rest of your area. Shoppers see the price on the product page, before they check out.
        </p>
      </div>

      <fieldset className="space-y-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
        <legend className="px-1 text-sm font-semibold text-neutral-800 dark:text-neutral-100">Nearby deliveries</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("nearFee", "Delivery fee", "What every nearby order pays. Enter 0 to deliver free.", { type: "number", min: 0, max: 100000, step: "0.01" })}
          {field("nearRadiusKm", "Deliver this far", "In kilometres from your dispatch location.", { type: "number", min: 0.1, max: 500, step: "0.1" })}
        </div>
      </fieldset>

      <fieldset className="space-y-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
        <legend className="px-1">
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            <input type="checkbox" checked={form.hasFarZone} onChange={set("hasFarZone")} className="h-4 w-4 rounded border-neutral-300" />
            Deliver further, for a higher fee
          </label>
        </legend>
        {form.hasFarZone ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {field("farFee", "Fee beyond the nearby zone", "A longer trip costs your rider more.", { type: "number", min: 0, max: 100000, step: "0.01" })}
            {field("serviceRadiusKm", "Stop delivering past", "Orders further out than this are turned away before payment.", { type: "number", min: 0.1, max: 500, step: "0.1" })}
          </div>
        ) : (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            You only deliver within {Number.isFinite(near) ? near : "—"} km. Anything further is turned away before payment.
          </p>
        )}
      </fieldset>

      <fieldset className="space-y-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
        <legend className="px-1">
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            <input type="checkbox" checked={form.hasFreeDelivery} onChange={set("hasFreeDelivery")} className="h-4 w-4 rounded border-neutral-300" />
            Free delivery on larger orders
          </label>
        </legend>
        {form.hasFreeDelivery
          ? field("freeDeliveryOver", "Free delivery over", "You absorb the fee on orders at or above this amount.", { type: "number", min: 1, step: "0.01" })
          : <p className="text-sm text-neutral-500 dark:text-neutral-400">Every order pays the delivery fee.</p>}
      </fieldset>

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm dark:border-neutral-700 dark:bg-neutral-800/60">
        <p className="font-semibold text-neutral-800 dark:text-neutral-100">What a shopper pays</p>
        {zonesCoherent ? (
          <>
            <ul className="mt-2 space-y-1 text-neutral-600 dark:text-neutral-300">
              <li>Up to {near} km &mdash; <strong>{peso(form.nearFee)}</strong></li>
              {form.hasFarZone && (
                <li>{near} to {outerKm} km &mdash; <strong>{peso(form.farFee)}</strong></li>
              )}
              <li>Past {outerKm} km &mdash; <strong>not available</strong></li>
              {form.hasFreeDelivery && <li>Orders over {peso(form.freeDeliveryOver)} &mdash; <strong>free</strong></li>}
            </ul>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Distance is measured along the road from your dispatch location.
            </p>
          </>
        ) : (
          // Never render a backwards range like "40 to 30 km" as though it were
          // real pricing. Fix the distances above and this fills in.
          <p className="mt-2 text-neutral-500 dark:text-neutral-400">
            Fill in the distances above to see what a shopper will be charged.
          </p>
        )}
      </div>


      {active ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100">
          Version {active.versionNo} is live. Activated pricing stays unchanged for orders already placed under it.
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Shoppers cannot be quoted for delivery until you save and activate pricing.
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <BtnPrimary onClick={saveDraft} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {latestDraft ? "Save changes" : "Save draft"}
        </BtnPrimary>
        {latestDraft && (
          <BtnPrimary onClick={() => activate(latestDraft.id)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleDollarSign className="h-4 w-4" />}
            Make version {latestDraft.versionNo} live
          </BtnPrimary>
        )}
      </div>
    </div>
  );
}

const BLANK_DISPATCH = {
  label: "Primary dispatch location",
  formattedAddress: "",
  addressLine1: "",
  addressLine2: "",
  barangay: "",
  city: "",
  province: "",
  postalCode: "",
  countryCode: "PH",
  latitude: null,
  longitude: null,
  pinConfirmed: false,
  source: "MANUAL_PIN",
  quality: "CONFIRMED",
  provider: "GOOGLE_MAPS",
  providerPlaceId: null,
  accuracyM: null,
  expectedVersion: 0,
};

function toDispatchForm(location) {
  if (!location) return { ...BLANK_DISPATCH };
  return {
    label: location.label || "Primary dispatch location",
    formattedAddress: location.formatted_address || "",
    addressLine1: location.address_line1 || "",
    addressLine2: location.address_line2 || "",
    barangay: location.barangay || "",
    city: location.city || "",
    province: location.province || "",
    postalCode: location.postal_code || "",
    countryCode: location.country_code || "PH",
    latitude: location.latitude == null ? null : Number(location.latitude),
    longitude: location.longitude == null ? null : Number(location.longitude),
    pinConfirmed: Boolean(location.pin_confirmed_at) && location.quality === "CONFIRMED",
    source: location.source || "MANUAL_PIN",
    quality: location.quality || "CONFIRMED",
    provider: location.provider || "GOOGLE_MAPS",
    providerPlaceId: location.provider_place_id || null,
    accuracyM: location.accuracy_m == null ? null : Number(location.accuracy_m),
    expectedVersion: Number(location.version || 0),
  };
}

export function DispatchLocationTab() {
  const [form, setForm] = useState(BLANK_DISPATCH);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restricted, setRestricted] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setRestricted(false);
    getBusinessDispatchLocation()
      .then((response) => setForm(toDispatchForm(response.data)))
      .catch((error) => {
        if (Number(error?.response?.status) === 403) setRestricted(true);
        else Swal.fire({ icon: "error", title: "Unable to load dispatch location" });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const handleSave = async () => {
    if (!form.label.trim() || !form.formattedAddress.trim() || !form.city.trim() || !form.province.trim()) {
      Swal.fire({ icon: "info", title: "Complete the required address fields" });
      return;
    }
    if (!form.pinConfirmed) {
      Swal.fire({ icon: "info", title: "Confirm the dispatch pin on the map" });
      return;
    }
    setSaving(true);
    try {
      // Moving the store is not a plain settings edit: ready parcels and riders
      // who have not set off follow it. Show the owner what that means before
      // they commit. A first-time setup has nothing to affect.
      if (Number(form.expectedVersion) > 0) {
        const impact = await getDispatchLocationChangeImpact(form).catch(() => null);
        const lines = describeOriginChangeImpact(impact);
        if (lines.length) {
          const decision = await Swal.fire({
            icon: "warning",
            title: "This will change active deliveries",
            html: `<ul style="text-align:left;margin:0;padding-left:1.1rem">${lines.map((line) => `<li style="margin:.35rem 0">${escapeHtml(line)}</li>`).join("")}</ul>`,
            showCancelButton: true,
            confirmButtonText: "Move my dispatch location",
            cancelButtonText: "Keep the current location",
            focusCancel: true,
          });
          if (!decision.isConfirmed) return;
        }
      }
      const idempotencyKey = window.crypto?.randomUUID?.()
        || `dispatch-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const response = await putBusinessDispatchLocation(form, idempotencyKey);
      setForm(toDispatchForm(response.location));
      const outcome = describeOriginChangeResult(response);
      Swal.fire({
        icon: "success",
        title: response.unchanged ? "No changes to save" : "Dispatch location saved",
        text: outcome || undefined,
        timer: outcome ? undefined : 1400,
        showConfirmButton: Boolean(outcome),
      });
    } catch (error) {
      if (Number(error?.response?.status) === 409) load();
      Swal.fire({
        icon: "error",
        title: "Unable to save dispatch location",
        text: error?.response?.data?.message || "Please review the location and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  if (restricted) return (
    <div className="max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
      You do not have permission to manage this business's delivery setup.
    </div>
  );

  const fieldClass = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";
  const fields = [
    ["label", "Location label", true], ["formattedAddress", "Formatted address", true],
    ["addressLine1", "Street / building", false], ["addressLine2", "Unit / floor", false],
    ["barangay", "Barangay", false], ["city", "City / municipality", true],
    ["province", "Province", true], ["postalCode", "Postal code", false],
  ];

  return (
    <div className="max-w-2xl space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
      <div>
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">1. Dispatch location</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Choose the store or warehouse location where delivery routes begin.</p>
      </div>
      {form.pinConfirmed && Number(form.expectedVersion) > 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100">
          Dispatch location is configured with a confirmed map pin.
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Add the dispatch address and confirm its map pin before delivery quoting can work.
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map(([key, label, required]) => (
          <label key={key} className={key === "formattedAddress" ? "sm:col-span-2" : ""}>
            <span className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-400">{label}{required ? " *" : ""}</span>
            <input value={form[key]} onChange={(event) => update(key, event.target.value)} className={fieldClass} />
          </label>
        ))}
      </div>
      <ConfirmedPinPicker
        value={form}
        onChange={(location) => setForm((current) => ({
          ...current,
          ...location,
          source: location.locationSource,
          quality: location.locationQuality,
          provider: location.geocodeProvider,
        }))}
      />
      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Dispatch Location
        </BtnPrimary>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PROFILE TAB
   ═══════════════════════════════════════════ */
function ProfileTab({ onOpenTrustTab }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuspended = String(profile?.status || "").toUpperCase() === "SUSPENDED";

  useEffect(() => {
    setLoading(true);
    getBusinessProfile()
      .then((res) => {
        const d = res.data;
        setProfile(d);
        setForm({ name: d.name || "", description: d.description || "" });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (isSuspended) {
      Swal.fire({
        icon: "info",
        title: "Profile editing unavailable",
        text: "Profile updates are locked while the business is suspended. Review the Trust & Safety tab for the current restriction and appeal flow.",
      });
      return;
    }
    setSaving(true);
    try {
      const res = await updateBusinessProfile({ name: form.name.trim(), description: form.description.trim() });
      setProfile(res.data);
      Swal.fire({ icon: "success", title: "Profile updated", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to update", text: err?.response?.data?.message || "Something went wrong" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  }

  const dirty = profile && (form.name !== profile.name || form.description !== (profile.description || ""));

  return (
    <div className="max-w-xl space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Business Profile</h3>

      <div className="space-y-4">
        {isSuspended && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-950/30">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Profile editing is locked while this business is suspended.</p>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-100">
                  The backend currently blocks profile updates for suspended businesses. Review the Trust & Safety tab for the suspension details and appeal workflow.
                </p>
              </div>
              <button
                type="button"
                onClick={onOpenTrustTab}
                className="shrink-0 rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/40"
              >
                View Trust & Safety
              </button>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Business Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            disabled={isSuspended}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-400
                       disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:border-neutral-500 dark:disabled:bg-neutral-800/60 dark:disabled:text-neutral-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            disabled={isSuspended}
            rows={3}
            placeholder="Describe your business..."
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-400
                       resize-none disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:border-neutral-500 dark:disabled:bg-neutral-800/60 dark:disabled:text-neutral-500"
          />
        </div>

        {/* Read-only business details from application */}
        {(profile?.email || profile?.phone || profile?.tax_id || profile?.registration_number) && (
          <div className="space-y-2 rounded-xl border border-neutral-100 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-800/50">
            <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Registration Details</h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {profile.email && (
                <div>
                  <span className="text-neutral-400 dark:text-neutral-500">Email:</span>
                  <span className="ml-1 text-neutral-700 dark:text-neutral-300">{profile.email}</span>
                </div>
              )}
              {profile.phone && (
                <div>
                  <span className="text-neutral-400 dark:text-neutral-500">Phone:</span>
                  <span className="ml-1 text-neutral-700 dark:text-neutral-300">{profile.phone}</span>
                </div>
              )}
              {profile.tax_id && (
                <div>
                  <span className="text-neutral-400 dark:text-neutral-500">Tax ID:</span>
                  <span className="ml-1 text-neutral-700 dark:text-neutral-300">{profile.tax_id}</span>
                </div>
              )}
              {profile.registration_number && (
                <div>
                  <span className="text-neutral-400 dark:text-neutral-500">Reg #:</span>
                  <span className="ml-1 text-neutral-700 dark:text-neutral-300">{profile.registration_number}</span>
                </div>
              )}
            </div>
            {profile.address && (
              <div className="text-xs">
                <span className="text-neutral-400 dark:text-neutral-500">Address:</span>
                <span className="ml-1 text-neutral-700 dark:text-neutral-300">
                  {[profile.address.line1, profile.address.line2, profile.address.city, profile.address.province, profile.address.postalCode].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-neutral-400">
          Status: <span className="font-medium text-neutral-600 dark:text-neutral-300">{profile?.status || "—"}</span>
          <span className="mx-2">·</span>
          Created: <span className="font-medium text-neutral-600 dark:text-neutral-300">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"}</span>
        </div>
      </div>

      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving || !dirty || isSuspended}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isSuspended ? "Profile Locked" : "Save Changes"}
        </BtnPrimary>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   NOTIFICATIONS TAB
   ═══════════════════════════════════════════ */
function NotificationsTab() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getBusinessNotificationSettings()
      .then((res) => setPrefs(res.preferences || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (category, channel) => {
    setPrefs((prev) =>
      prev.map((p) => (p.category === category && p.channel === channel && !p.required ? { ...p, enabled: !p.enabled } : p))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateBusinessNotificationPreferences(prefs);
      setPrefs(res.preferences || prefs);
      Swal.fire({ icon: "success", title: "Preferences saved", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to save", text: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  }

  // Group by category
  const categories = Object.keys(CATEGORY_LABELS);
  const grouped = {};
  for (const cat of categories) grouped[cat] = prefs.filter((p) => p.category === cat);

  return (
    <div className="max-w-2xl space-y-5">
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Control how you receive business notifications. In-App notifications marked as required cannot be disabled.
      </p>

      {categories.map((cat) => (
        <div key={cat} className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-3">
            {CATEGORY_LABELS[cat]}
          </h4>
          <div className="grid grid-cols-5 gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-2 px-1">
            <span />
            {Object.values(CHANNEL_LABELS).map((ch) => (
              <span key={ch} className="text-center font-medium">{ch}</span>
            ))}
          </div>
          {grouped[cat]?.length ? (
            <div className="grid grid-cols-5 gap-2 items-center px-1">
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{CATEGORY_LABELS[cat]}</span>
              {Object.keys(CHANNEL_LABELS).map((ch) => {
                const pref = grouped[cat].find((p) => p.channel === ch);
                if (!pref) return <span key={ch} />;
                return (
                  <div key={ch} className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => toggle(cat, ch)}
                      disabled={pref.required}
                      className={[
                        "h-5 w-9 rounded-full transition-colors relative",
                        pref.enabled
                          ? "bg-emerald-500 dark:bg-emerald-600"
                          : "bg-neutral-300 dark:bg-neutral-600",
                        pref.required ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
                          pref.enabled ? "translate-x-4" : "translate-x-0.5",
                        ].join(" ")}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-neutral-400">No preferences available.</p>
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Preferences
        </BtnPrimary>
      </div>
    </div>
  );
}
